import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { erpApi, formatApiErrorDetail } from "@/lib/erpApi";

export const ChangeOwnPasswordDialog = ({ open, onOpenChange }) => {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setCur("");
    setNext("");
    setConfirm("");
  };

  const close = () => {
    onOpenChange(false);
    reset();
  };

  const submit = async () => {
    if (next.length < 6) {
      toast.error("Parola nouă trebuie să aibă minim 6 caractere");
      return;
    }
    if (next !== confirm) {
      toast.error("Parolele nu coincid");
      return;
    }
    setSaving(true);
    try {
      await erpApi.post("/account/change-password", {
        current_password: cur,
        new_password: next,
      });
      toast.success("Parola ta a fost schimbată");
      close();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        onOpenChange(o);
        if (!o) reset();
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Schimbă parola mea</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-slate-500">
            Ai nevoie de parola actuală. Dacă ai uitat-o, roagă un administrator să ți-o reseteze.
          </p>
          <div>
            <Label className="font-semibold">Parola actuală</Label>
            <Input
              type="password"
              data-testid="erp-own-current-password"
              value={cur}
              onChange={(e) => setCur(e.target.value)}
              className="mt-1.5"
              autoComplete="current-password"
            />
          </div>
          <div>
            <Label className="font-semibold">Parolă nouă</Label>
            <Input
              type="password"
              data-testid="erp-own-new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              placeholder="min. 6 caractere"
              className="mt-1.5"
              autoComplete="new-password"
            />
          </div>
          <div>
            <Label className="font-semibold">Confirmă parola</Label>
            <Input
              type="password"
              data-testid="erp-own-confirm-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="repetă parola"
              className="mt-1.5"
              autoComplete="new-password"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            Anulează
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            data-testid="erp-own-password-save"
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Schimbă parola"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ChangeOwnPassword = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="erp-change-own-password-button"
        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100 transition-colors duration-200"
      >
        <KeyRound className="w-5 h-5 shrink-0" /> Schimbă parola mea
      </button>
      <ChangeOwnPasswordDialog open={open} onOpenChange={setOpen} />
    </>
  );
};
